import FormActions from '@viking-eng/form-actions';
import InputText from '@viking-eng/input-text';
import Select from '@viking-eng/select';
import PropTypes from 'prop-types';
import { change, Field, Form } from 'redux-form';

import { APP_PAGES, B2C_USER_TYPES, EDIT_FORM_TYPE, EDIT_NAME_STEPS, FORMS } from 'common/Constants';
import { ERROR_CODES, ERROR_MESSAGES, required } from 'common/forms/Validations';
import BookingActionModal from 'components/BookingActionModal/BookingActionModal';
import ConfirmEmailForm from 'components/ConfirmEmailForm/ConfirmEmailForm';

const EditName = ({
  bookingId,
  cruiseName,
  editNames,
  error,
  formStep,
  handleSubmit,
  id,
  initialValues: { firstName, lastName, userType },
  onClose,
  pristine,
  submitting,
  user,
  userTypeOptions,
}) => {
  const formType = window.location?.pathname?.startsWith(APP_PAGES.USERS.path)
    ? EDIT_FORM_TYPE.B2C_USER
    : EDIT_FORM_TYPE.MVJ_USER;

  const formName = formType === EDIT_FORM_TYPE.B2C_USER ? 'Account Details' : 'Account Name';

  const title = formStep === EDIT_NAME_STEPS.CONFIRM_EDIT ? 'Are you sure?' : `Edit ${formName}`;
  const primaryButton = {};
  const secondaryButton = {};
  const cancelFunction = () => window.$(`#${FORMS.EDIT_NAME}`).modal('hide');
  const FORM_ELEMENTS = {
    [EDIT_NAME_STEPS.CONFIRM_EDIT]: {
      primaryButtonText: 'SUBMIT',
      secondaryButtonText: 'CANCEL',
    },
    [EDIT_NAME_STEPS.CONFIRM_EMAIL]: {
      primaryButtonText: 'SUBMIT',
      secondaryButtonFunction: null,
      secondaryButtonText: null,
    },
    [EDIT_NAME_STEPS.EDIT]: {
      primaryButtonText: 'SAVE',
      secondaryButtonText: 'CANCEL',
    },
  };
  primaryButton.text = FORM_ELEMENTS[formStep]?.primaryButtonText;
  secondaryButton.text = FORM_ELEMENTS[formStep]?.secondaryButtonText;
  secondaryButton.onButtonClick = cancelFunction;

  const getChangedDataItem = ({ fieldName, newValue, oldValue }) => {
    return oldValue !== newValue ? (
      <div className="edit-name-detail-wrapper row no-gutters d-block">
        <div className="edit-name-title col-3">{`${fieldName}:`}</div>
        <div className="edit-name-value col-6">
          <span className="previous-value">{`${oldValue} to `}</span>
          {newValue}
        </div>
      </div>
    ) : null;
  };
  return (
    <BookingActionModal id={id} title={title} onClose={onClose}>
      {user && user.firstName && (
        <Form className="form confirm-email-form" name="editname" onSubmit={handleSubmit}>
          {formStep === EDIT_NAME_STEPS.CONFIRM_EMAIL && (
            <ConfirmEmailForm
              bookingId={bookingId}
              cruiseName={cruiseName}
              user={user}
              onEmailChange={(evt) => {
                change('editname', 'email', (evt.target.value || '').trim());
              }}
            />
          )}
          {formStep === EDIT_NAME_STEPS.EDIT && (
            <div className="row no-gutters edit-name-fields">
              <div className="edit-name-form-input-wrapper">
                <div className="col-md-7 m-auto">
                  {formType === EDIT_FORM_TYPE.B2C_USER ? (
                    <Field
                      component={Select}
                      id="userType"
                      name="userType"
                      options={userTypeOptions}
                      placeholder="User Type"
                      value={userTypeOptions.find((opt) => opt.value === userType)}
                    />
                  ) : null}
                  <Field
                    component={InputText}
                    errorCodes={{
                      [ERROR_CODES.REQUIRED]: ERROR_CODES.FIRST_NAME_REQUIRED,
                    }}
                    errorMessages={ERROR_MESSAGES}
                    id="firstName"
                    name="firstName"
                    placeholder="First Name"
                    validate={[required]}
                  />
                  <Field
                    component={InputText}
                    errorCodes={{
                      [ERROR_CODES.REQUIRED]: ERROR_CODES.LAST_NAME_REQUIRED,
                    }}
                    errorMessages={ERROR_MESSAGES}
                    id="lastName"
                    name="lastName"
                    placeholder="Last Name"
                    validate={[required]}
                  />
                </div>
              </div>
            </div>
          )}
          {formStep === EDIT_NAME_STEPS.CONFIRM_EDIT && (
            <div className="subtitle">
              <div className="mb-3">You are changing the following values on this account:</div>
              <div className="row no-gutters edit-name-fields m-0 w-100">
                <div className="col-12">
                  {getChangedDataItem({ fieldName: 'User Type', oldValue: userType, newValue: editNames.userType })}
                  {getChangedDataItem({
                    fieldName: 'First Name',
                    oldValue: firstName,
                    newValue: editNames.firstName,
                  })}
                  {getChangedDataItem({ fieldName: 'Last Name', oldValue: lastName, newValue: editNames.lastName })}
                </div>
              </div>
              <div className="mt-2">Would you like to continue?</div>
              {formType === EDIT_FORM_TYPE.B2C_USER &&
                editNames.userType === B2C_USER_TYPES.TRAVEL_ADVISOR.value &&
                userType !== B2C_USER_TYPES.TRAVEL_ADVISOR.value && (
                  <div className="mt-4">
                    <b>NOTE:</b>
                    &nbsp;
                    <i>
                      Please verify that a record exists for this agent in the Dynamo Travel Agents table before
                      submitting change.
                    </i>
                  </div>
                )}
            </div>
          )}
          <FormActions
            alert={error}
            primaryButton={{
              disabled: pristine,
              loading: submitting,
              ...primaryButton,
            }}
            secondaryButton={secondaryButton}
          />
        </Form>
      )}
    </BookingActionModal>
  );
};

EditName.propTypes = {
  bookingId: PropTypes.string,
  cruiseName: PropTypes.string,
  editNames: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userType: PropTypes.string,
  }).isRequired,
  error: PropTypes.string,
  formStep: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  id: PropTypes.string,
  initialValues: PropTypes.shape({
    email: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userType: PropTypes.string,
  }).isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  user: PropTypes.shape({
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    userType: PropTypes.string,
  }).isRequired,
  userTypeOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    }),
  ),
  onClose: PropTypes.func.isRequired,
};

EditName.defaultProps = {
  bookingId: null,
  cruiseName: null,
  error: '',
  formStep: 'confirmEmail',
  id: '',
  userTypeOptions: [],
};

export default EditName;
